import type { ModuleKey, ReadinessState } from '../../api/supervisionApi';

/**
 * Where a module's full analytics live.
 *
 *   'agency' — the deep view renders here, in agency chrome. There is no
 *              client-side equivalent (the review funnel is agency-only).
 *   'client' — the module's real screen lives in the practice's own app, so
 *              opening it starts an impersonated session. Labelled, so nobody
 *              lands in a client account by accident.
 */
export interface ModuleMeta {
  label: string;
  tone: string;
  deepLabel: string;
  where: 'agency' | 'client';
  /** Route inside the practice's own app. Null for agency-native views. */
  clientPath: string | null;
}

export const MODULE_META: Record<ModuleKey, ModuleMeta> = {
  visibility: {
    label: 'Online Visibility',
    tone: 'info',
    deepLabel: 'Audit detail',
    where: 'client',
    clientPath: '/seo/audit',
  },
  reviews: {
    label: 'Review Accelerator',
    tone: 'success',
    deepLabel: 'Funnel',
    where: 'agency',
    clientPath: null,
  },
  ads: {
    label: 'Ad Campaigns',
    tone: 'purple',
    deepLabel: 'Campaigns',
    where: 'client',
    clientPath: '/campaigns/list',
  },
  calls: {
    label: 'Front Desk Calls',
    tone: 'accent',
    deepLabel: 'Call log',
    where: 'client',
    clientPath: '/calls/log',
  },
  safety: {
    label: 'Safety & Training',
    tone: 'warning',
    deepLabel: 'Training',
    where: 'client',
    clientPath: '/safety/training',
  },
  insurance: {
    label: 'Insurance Verification',
    tone: 'info',
    deepLabel: 'Payers',
    where: 'client',
    clientPath: '/insurance/payers',
  },
  strategy: {
    label: 'Strategy',
    tone: 'purple',
    deepLabel: 'Strategy',
    where: 'client',
    clientPath: '/strategy',
  },
};

export const READINESS_META: Record<ReadinessState, { glyph: string; tone: string }> = {
  done: { glyph: '✓', tone: 'success' },
  blocked: { glyph: '✗', tone: 'error' },
  pending: { glyph: '○', tone: 'muted' },
  na: { glyph: '–', tone: 'muted' },
};

/** "3h" / "2d" / "just now". An age, never a countdown. */
export function formatWaiting(iso: string | null): string {
  if (!iso) return '—';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
